export type ErrorType = 
  | 'PasswordSignInFailed'
  | 'PasswordSignUpFailed'
  | 'ActionErrorLoadUserNoUser'
  | 'UserNotFound'
  | 'InvalidPassword'
  | 'WeakPassword'
  | 'PasswordMismatch'
  | 'EmailNotVerified'
  | 'InvalidEmail'
  | 'EmailAlreadyExists'
  | 'InvalidVerificationCode'
  | 'VerificationCodeExpired'
  | 'TooManyRequests'
  | 'RateLimitExceeded'
  | 'OAuthLoginFailed'
  | 'OAuthNotConfigured'
  | 'GoogleOAuthFailed'
  | 'GitHubOAuthFailed'
  | 'MagicLinkSendFailed'
  | 'MagicLinkExpired'
  | 'InvalidMagicLink'
  | 'SessionExpired'
  | 'InvalidSession'
  | 'TokenExpired'
  | 'NetworkError'
  | 'ServerError'
  | 'ServiceUnavailable'
  | 'Timeout'
  | 'InvalidInput'
  | 'ValidationFailed'
  | 'Unauthorized'
  | 'Forbidden'
  | 'BadRequest'
  | 'AccountLocked'
  | 'AccountDisabled'
  | 'AccountSuspended'
  | 'PasswordResetFailed'
  | 'InvalidResetToken'
  | 'ResetTokenExpired'
  | string;

export type ErrorSeverity = 'critical' | 'warning' | 'error';

export interface ErrorObject {
  type?: string;
  code?: string;
  name?: string;
  message?: string;
  text?: string;
  description?: string;
}

export type ErrorInput = string | ErrorObject | Error;

export const createErrorTransformer = () => {
  const errorTranslationMap: Record<string, string> = {
    'PasswordSignInFailed': 'Incorrect email or password. Please try again.',
    'PasswordSignUpFailed': 'Sign up failed. Please check your password requirements.',
    'ActionErrorLoadUserNoUser': 'No account found with this email address.',
    'UserNotFound': 'No account found with this email address.',
    'InvalidPassword': 'Incorrect password. Please try again.',
    'WeakPassword': 'Password is too weak. Please use a stronger password with at least 8 characters.',
    'PasswordMismatch': 'Passwords do not match. Please try again.',
    'EmailNotVerified': 'Please verify your email address before signing in.',
    'InvalidEmail': 'Please enter a valid email address.',
    'EmailAlreadyExists': 'An account with this email already exists. Try signing in instead.',
    'InvalidVerificationCode': 'Invalid verification code. Please try again.',
    'VerificationCodeExpired': 'Verification code has expired. Please request a new one.',
    'TooManyRequests': 'Too many attempts. Please wait a few minutes before trying again.',
    'RateLimitExceeded': 'Too many requests. Please try again later.',
    'OAuthLoginFailed': 'Social login failed. Please try again or use email/password.',
    'OAuthNotConfigured': 'Social login is not configured. Please contact support or use email/password.',
    'GoogleOAuthFailed': 'Google sign in failed. Please try again or use email/password.',
    'GitHubOAuthFailed': 'GitHub sign in failed. Please try again or use email/password.',
    'MagicLinkSendFailed': 'Failed to send magic link. Please try again.',
    'MagicLinkExpired': 'Magic link has expired. Please request a new one.',
    'InvalidMagicLink': 'Invalid magic link. Please request a new one.',
    'SessionExpired': 'Your session has expired. Please sign in again.',
    'InvalidSession': 'Invalid session. Please sign in again.',
    'TokenExpired': 'Your session has expired. Please sign in again.',
    'NetworkError': 'Network error. Please check your connection and try again.',
    'ServerError': 'Server error. Please try again later.',
    'ServiceUnavailable': 'Service temporarily unavailable. Please try again later.',
    'Timeout': 'Request timed out. Please try again.',
    'InvalidInput': 'Please check your input and try again.',
    'ValidationFailed': 'Please fill in all required fields correctly.',
    'Unauthorized': 'You are not authorized to perform this action.',
    'Forbidden': 'Access denied. Please contact support if you believe this is an error.',
    'BadRequest': 'Invalid request. Please try again.',
    'AccountLocked': 'Your account has been temporarily locked. Please contact support.',
    'AccountDisabled': 'Your account is disabled. Please contact support.',
    'AccountSuspended': 'Your account is suspended. Please contact support.',
    'PasswordResetFailed': 'Failed to send password reset email. Please try again.',
    'InvalidResetToken': 'Invalid or expired reset link. Please request a new one.',
    'ResetTokenExpired': 'Password reset link has expired. Please request a new one.'
  };

  return (error: ErrorInput): string => {
    let errorType = '';
    let errorMessage = '';
    
    if (typeof error === 'string') {
      errorType = error;
      errorMessage = error;
    } else if (error && typeof error === 'object') {
      const errorObj = error as ErrorObject;
      errorType = errorObj.type || errorObj.code || errorObj.name || '';
      errorMessage = errorObj.message || errorObj.text || errorObj.description || '';
    }

    const friendlyMessage = errorTranslationMap[errorType];
    
    if (friendlyMessage) {
      return friendlyMessage;
    }
    
    if (errorMessage) {
      if (errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('incorrect')) {
        return 'Incorrect password. Please try again.';
      }
      
      if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('exists')) {
        return 'An account with this email already exists. Try signing in instead.';
      }
      
      if (errorMessage.toLowerCase().includes('user') && errorMessage.toLowerCase().includes('not found')) {
        return 'No account found with this email address.';
      }
      
      if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('connection')) {
        return 'Network error. Please check your connection and try again.';
      }
      
      if (errorMessage.length > 5 && !errorMessage.includes('Error:') && !errorMessage.includes('Exception:')) {
        return errorMessage;
      }
    }
    
    return 'Something went wrong. Please try again or contact support if the problem persists.';
  };
};

export const errorTransformer = createErrorTransformer();

export const shouldDisplayError = (error: ErrorInput): boolean => {
  const hiddenErrorTypes = [
    'NetworkTimeoutError',
    'AbortError',
    'CorsError',
    'UnknownError'
  ];
  
  const errorObj = error as ErrorObject;
  const errorType = errorObj?.type || errorObj?.code || errorObj?.name || '';
  return !hiddenErrorTypes.includes(errorType);
};

export const getErrorSeverity = (error: ErrorInput): ErrorSeverity => {
  const errorObj = error as ErrorObject;
  const errorType = errorObj?.type || errorObj?.code || errorObj?.name || '';
  
  const criticalErrors = [
    'AccountLocked',
    'AccountDisabled',
    'AccountSuspended',
    'SecurityViolation'
  ];
  
  const warningErrors = [
    'WeakPassword',
    'EmailNotVerified',
    'RateLimitExceeded'
  ];
  
  if (criticalErrors.includes(errorType)) {
    return 'critical';
  }
  
  if (warningErrors.includes(errorType)) {
    return 'warning';
  }
  
  return 'error';
};
